import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { directus } from "@/lib/directus";
import { updateItem, readItems } from "@directus/sdk";

export interface OrganizationProfile {
  // From ID.gov (КЕП) - potentially READ-ONLY
  head_full_name: string; // ПІБ керівника = authenticated user's name
  edrpou_code: string; // ЄДРПОУ - from КЕП certificate
  organization_name: string; // Назва організації - from КЕП certificate if available

  // Contact info - pre-filled from ID.gov but can be different for organization
  phone: string;
  email: string;

  // User enters - organization specific data
  recipient_type: "osbb" | "manager" | "cooperative" | null;
  legal_address: string;
  actual_address: string;
  iban: string;
}

// Track which fields came from ID.gov (read-only)
export interface IdGovFields {
  head_full_name: boolean;
  edrpou_code: boolean;
  organization_name: boolean;
  phone: boolean;
  email: boolean;
}

const emptyProfile: OrganizationProfile = {
  recipient_type: null,
  organization_name: "",
  edrpou_code: "",
  legal_address: "",
  actual_address: "",
  phone: "+380",
  email: "",
  iban: "",
  head_full_name: "",
};

const emptyIdGovFields: IdGovFields = {
  head_full_name: false,
  edrpou_code: false,
  organization_name: false,
  phone: false,
  email: false,
};

interface ProfileContextValue {
  profile: OrganizationProfile;
  idGovFields: IdGovFields; // Which fields are from ID.gov (read-only)
  updateProfile: (data: Partial<OrganizationProfile>) => void;
  saveProfile: () => Promise<void>;
  clearProfile: () => void;
  isProfileComplete: boolean;
  hasStoredProfile: boolean;
  isSaving: boolean;
  isLoading: boolean;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useProfile() {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfile must be used within ProfileProvider");
  }
  return context;
}

interface ProfileProviderProps {
  children: ReactNode;
}

// Interface for idgov_users record with profile fields
interface IdGovUserWithProfile {
  id: string;
  profile_recipient_type?: string | null;
  profile_legal_address?: string | null;
  profile_actual_address?: string | null;
  profile_org_phone?: string | null;
  profile_org_email?: string | null;
  profile_iban?: string | null;
  profile_edrpou?: string | null;
  profile_organization_name?: string | null;
}

export function ProfileProvider({ children }: ProfileProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState<OrganizationProfile>(emptyProfile);
  const [idGovFields, setIdGovFields] = useState<IdGovFields>(emptyIdGovFields);
  const [hasStoredProfile, setHasStoredProfile] = useState(false);
  const [idGovDataApplied, setIdGovDataApplied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userIdGovId, setUserIdGovId] = useState<string | null>(null);

  // Load profile from Directus ONLY when authenticated
  useEffect(() => {
    async function loadProfileFromDirectus() {
      if (!isAuthenticated || !user) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        // Get idgov_user record for current user
        const records = await directus.request(
          readItems("idgov_users", {
            filter: { directus_user_id: { _eq: "$CURRENT_USER" } },
            fields: [
              "id",
              "profile_recipient_type",
              "profile_legal_address",
              "profile_actual_address",
              "profile_org_phone",
              "profile_org_email",
              "profile_iban",
              "profile_edrpou",
              "profile_organization_name",
            ],
            limit: 1,
          })
        ) as IdGovUserWithProfile[];

        const record = records[0];
        if (record) {
          setUserIdGovId(record.id);

          // Check if any profile data exists in Directus
          const hasServerProfile = !!(
            record.profile_recipient_type ||
            record.profile_legal_address ||
            record.profile_org_phone ||
            record.profile_org_email ||
            record.profile_iban ||
            record.profile_edrpou ||
            record.profile_organization_name
          );

          if (hasServerProfile) {
            // Load profile from Directus
            setProfile((prev) => ({
              ...prev,
              recipient_type: (record.profile_recipient_type as OrganizationProfile["recipient_type"]) || null,
              legal_address: record.profile_legal_address || "",
              actual_address: record.profile_actual_address || "",
              phone: record.profile_org_phone || "+380",
              email: record.profile_org_email || "",
              iban: record.profile_iban || "",
              // Only use profile_ fields if ID.gov didn't provide them
              edrpou_code: prev.edrpou_code || record.profile_edrpou || "",
              organization_name: prev.organization_name || record.profile_organization_name || "",
            }));
            setHasStoredProfile(true);
          }
        }
      } catch {
        // Silent fail - profile will be empty
      } finally {
        setIsLoading(false);
      }
    }

    loadProfileFromDirectus();
  }, [isAuthenticated, user]);

  // Apply ID.gov data when user is available
  useEffect(() => {
    if (user && !idGovDataApplied) {
      const newIdGovFields: IdGovFields = { ...emptyIdGovFields };

      // ПІБ керівника - always from ID.gov (authenticated user's name)
      const fullName = [user.lastname, user.givenname, user.middlename]
        .filter(Boolean)
        .join(" ");
      if (fullName) {
        newIdGovFields.head_full_name = true;
      }

      // ЄДРПОУ - from ID.gov if provided (organizational auth / КЕП)
      if (user.edrpoucode) {
        newIdGovFields.edrpou_code = true;
      }

      // Organization name - from КЕП certificate if provided
      if (user.organization_name) {
        newIdGovFields.organization_name = true;
      }

      // Email - from ID.gov if provided
      if (user.email) {
        newIdGovFields.email = true;
      }

      // Phone - from ID.gov if provided
      if (user.phone) {
        newIdGovFields.phone = true;
      }

      setIdGovFields(newIdGovFields);

      // Pre-fill profile with ID.gov data (only if not already loaded from Directus)
      setProfile((prev) => ({
        ...prev,
        head_full_name: prev.head_full_name || fullName || "",
        edrpou_code: prev.edrpou_code || user.edrpoucode || "",
        organization_name: prev.organization_name || user.organization_name || "",
        email: prev.email || user.email || "",
        phone: prev.phone === "+380" ? (user.phone || "+380") : prev.phone,
      }));

      setIdGovDataApplied(true);
    }
  }, [user, idGovDataApplied]);

  const updateProfile = useCallback(
    (data: Partial<OrganizationProfile>) => {
      // Filter out read-only fields from ID.gov
      const filteredData = { ...data };

      if (idGovFields.head_full_name && "head_full_name" in filteredData) {
        delete filteredData.head_full_name;
      }
      if (idGovFields.edrpou_code && "edrpou_code" in filteredData) {
        delete filteredData.edrpou_code;
      }
      if (idGovFields.organization_name && "organization_name" in filteredData) {
        delete filteredData.organization_name;
      }
      // Note: email and phone CAN be different for organization contact,
      // so we allow editing even if pre-filled from ID.gov

      setProfile((prev) => ({ ...prev, ...filteredData }));
    },
    [idGovFields]
  );

  const saveProfile = useCallback(async () => {
    if (!userIdGovId) {
      throw new Error("User not authenticated");
    }

    setIsSaving(true);

    try {
      // Save to Directus ONLY
      // Only save EDRPOU and org name to profile_ fields if they weren't from ID.gov
      const updateData: Record<string, unknown> = {
        profile_recipient_type: profile.recipient_type,
        profile_legal_address: profile.legal_address || null,
        profile_actual_address: profile.actual_address || null,
        profile_org_phone: profile.phone || null,
        profile_org_email: profile.email || null,
        profile_iban: profile.iban || null,
      };
      
      // Save EDRPOU if not from ID.gov
      if (!idGovFields.edrpou_code) {
        updateData.profile_edrpou = profile.edrpou_code || null;
      }
      
      // Save organization name if not from ID.gov
      if (!idGovFields.organization_name) {
        updateData.profile_organization_name = profile.organization_name || null;
      }
      
      await directus.request(
        updateItem("idgov_users", userIdGovId, updateData)
      );
      setHasStoredProfile(true);
    } catch (error) {
      throw error; // Re-throw so UI can handle it
    } finally {
      setIsSaving(false);
    }
  }, [profile, userIdGovId, idGovFields]);

  const clearProfile = useCallback(async () => {
    if (!userIdGovId) {
      throw new Error("User not authenticated");
    }

    // Clear profile in Directus
    await directus.request(
      updateItem("idgov_users", userIdGovId, {
        profile_recipient_type: null,
        profile_legal_address: null,
        profile_actual_address: null,
        profile_org_phone: null,
        profile_org_email: null,
        profile_iban: null,
        profile_edrpou: null,
        profile_organization_name: null,
      })
    );
    setProfile(emptyProfile);
    setHasStoredProfile(false);
    setIdGovDataApplied(false);
  }, [userIdGovId]);

  // Check if profile has minimum required fields
  const isProfileComplete =
    profile.recipient_type !== null &&
    profile.organization_name.length >= 3 &&
    /^\d{8}$/.test(profile.edrpou_code) &&
    profile.head_full_name.length >= 5 &&
    profile.legal_address.length >= 10 &&
    /^\+380\d{9}$/.test(profile.phone.replace(/\s/g, "")) &&
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(profile.email) &&
    /^UA\d{27}$/.test(profile.iban);

  return (
    <ProfileContext.Provider
      value={{
        profile,
        idGovFields,
        updateProfile,
        saveProfile,
        clearProfile,
        isProfileComplete,
        hasStoredProfile,
        isSaving,
        isLoading,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}
